import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";
import AssignUsers from "./AssignUsers";
import ViewUsers from "./ViewUsers";
import vdmDashboardActions from "redux/app/vdmdashboard/actions";
import vdmDashboardSelectors from "redux/app/vdmdashboard/selectors";
import userSelectors from "redux/app/user/selectors";
import {
	TAssignView,
	TDepartmentID,
	TDepartmentWiseAssignment,
} from "redux/app/vdmdashboard/@types";
import { vdmDashboardUtils } from "redux/app/vdmdashboard/utils";
import classNames from "classnames";
const { createEmptyAssignment, withoutProfilePicture } = vdmDashboardUtils;

type OwnProps = {
	isAdmin?: boolean;
	vesselId: string;
	tenantId: string;
};

type PICDashboardProps = PropsFromRedux & OwnProps;

function PICDashboard(props: PICDashboardProps) {
	const {
		isAdmin,
		vesselId,
		isAssignmentLoading,
		isLoading: isJobLoading,
		isBeingUploaded,
		isBeingSaved,
		picDepartmentWiseAssignment,
	} = props;

	const [activeView, setActiveView] = useState<TAssignView>("view");
	const [activeDepartment, setActiveDepartment] =
		useState<TDepartmentID>("technical");

	// True once the assignment-load job has gone true -> false at least
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	const prevAssignmentLoadingRef = useRef(isAssignmentLoading);

	useEffect(() => {
		if (prevAssignmentLoadingRef.current && !isAssignmentLoading) {
			setHasLoadedOnce(true);
		}
		prevAssignmentLoadingRef.current = isAssignmentLoading;
	}, [isAssignmentLoading]);

	const isLoading = isJobLoading || !hasLoadedOnce;

	// switching tabs never does a refetch
	const baseline = picDepartmentWiseAssignment ?? createEmptyAssignment();
	const [localAssignment, setLocalAssignment] =
		useState<TDepartmentWiseAssignment>(baseline);

	const isDirty = useMemo(
		() =>
			JSON.stringify(withoutProfilePicture(localAssignment)) !==
			JSON.stringify(withoutProfilePicture(baseline)),
		[localAssignment, baseline],
	);

	const isDirtyRef = useRef(isDirty);
	isDirtyRef.current = isDirty;

	// True once real data has landed in localAssignment thru api call. So basically state will change with empty assignments but that doesn't mean really data.
	const isInitialSyncRef = useRef(false);

	// Tracks the vessel we last ran the sync effect for, so a vessel switch can be detected and handled within this single effect instead of
	const prevVesselIdRef = useRef<string | null>(null);

	// Full sync from global state -> local, plus vessel-switch handling.
	useEffect(() => {
		const vesselChanged = prevVesselIdRef.current !== vesselId;
		prevVesselIdRef.current = vesselId;

		if (vesselChanged) {
			// Reset local state for the new vessel and start its fetch.
			// Don't sync from picDepartmentWiseAssignment yet - at this point
			isInitialSyncRef.current = false;
			isDirtyRef.current = false;
			setLocalAssignment(createEmptyAssignment());
			props.loadDepartmentWiseAssignments(vesselId);
			return;
		}

		if (!picDepartmentWiseAssignment) return;

		if (!isInitialSyncRef.current) {
			isInitialSyncRef.current = true;
			setLocalAssignment(picDepartmentWiseAssignment);
			return;
		}

		if (!isDirtyRef.current) {
			setLocalAssignment(picDepartmentWiseAssignment);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [picDepartmentWiseAssignment, vesselId]);

	// personId -> objectURL for pictures picked but not yet reflected in the backend Lives here
	const [localPreviewUrls, setLocalPreviewUrls] = useState<
		Record<string, string>
	>({});

	const localPreviewUrlsRef = useRef(localPreviewUrls);
	localPreviewUrlsRef.current = localPreviewUrls;

	useEffect(() => {
		return () => {
			Object.values(localPreviewUrlsRef.current).forEach((url) =>
				URL.revokeObjectURL(url),
			);
		};
	}, []);

	const registerLocalPreview = (personId: string, url: string) => {
		setLocalPreviewUrls((prev) => {
			const old = prev[personId];
			if (old && old !== url) URL.revokeObjectURL(old);
			return { ...prev, [personId]: url };
		});
	};

	if (isAdmin) {
		return (
			<div className="w-full">
				<div className="w-full border-b border-b-[#EAECF0] flex gap-2 overflow-x-auto text-sm md:text-base justify-center mb-4">
					<button
						className={classNames(
							`px-4 py-2 rounded-sm font-medium whitespace-nowrap ${
								activeView === "assign"
									? "text-blue-500 border-b-2 border-blue-500"
									: "bg-white text-gray-700 border-b-2 border-transparent"
							}`,
							{
								"cursor-not-allowed": isLoading,
							},
						)}
						onClick={() => {
							if (isLoading) return;
							setActiveView("assign");
						}}
					>
						Manage PIC
					</button>
					<button
						className={classNames(
							`px-4 py-2 rounded-sm font-medium whitespace-nowrap ${
								activeView === "view"
									? "text-blue-500 border-b-2 border-blue-500"
									: "bg-white text-gray-700 border-b-2 border-transparent"
							}`,
							{
								"cursor-not-allowed": isLoading,
							},
						)}
						onClick={() => {
							if (isLoading) return;
							setActiveView("view");
						}}
					>
						View PIC
					</button>
				</div>
				{activeView === "assign" ? (
					<AssignUsers
						isBeingSaved={isBeingSaved}
						isBeingUploaded={isBeingUploaded}
						isLoading={isLoading}
						vesselId={vesselId}
						isAdmin={true}
						activeDepartment={activeDepartment}
						setActiveDepartment={setActiveDepartment}
						setActiveView={setActiveView}
						localAssignment={localAssignment}
						setLocalAssignment={setLocalAssignment}
						baseline={baseline}
						isDirty={isDirty}
						onPictureSelected={registerLocalPreview}
						localPicturePreviews={localPreviewUrls}
					/>
				) : (
					<ViewUsers
						isLoading={isLoading}
						vesselId={vesselId}
						activeDepartment={activeDepartment}
						setActiveDepartment={setActiveDepartment}
						localAssignment={localAssignment}
						localPicturePreviews={localPreviewUrls}
					/>
				)}
			</div>
		);
	}

	return (
		<ViewUsers
			activeDepartment={activeDepartment}
			setActiveDepartment={setActiveDepartment}
			isLoading={isLoading}
			vesselId={vesselId}
			localAssignment={localAssignment}
			localPicturePreviews={localPreviewUrls}
		/>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const vdmDashboardStore = vdmDashboardSelectors._getStore(store);
	const userStore = userSelectors._getStore(store);
	const isDepartmentWiseAssignmentLoading =
		vdmDashboardSelectors?.getJobToLoadVDMPICContent(
			vdmDashboardStore,
		).isLoading;
	const isAssignmentBeingSaved =
		vdmDashboardSelectors.getJobToVDMPICSave(vdmDashboardStore).isLoading;
	const isPICProfilePICBeingUploaded =
		vdmDashboardSelectors.getJobToUploadProfilePicture(
			vdmDashboardStore,
		).isLoading;
	const isUsersListBeingLoaded =
		userSelectors.getJobToLoadUsers(userStore).isLoading;

	const isLoading =
		isDepartmentWiseAssignmentLoading ||
		isAssignmentBeingSaved ||
		isPICProfilePICBeingUploaded ||
		isUsersListBeingLoaded;

	const picDepartmentWiseAssignment =
		vdmDashboardSelectors.getVDMPICDepartmentWiseAssignment(vdmDashboardStore);

	return {
		isBeingSaved: isAssignmentBeingSaved,
		isBeingUploaded: isPICProfilePICBeingUploaded,
		isAssignmentLoading: isDepartmentWiseAssignmentLoading,
		isLoading,
		picDepartmentWiseAssignment,
	};
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		loadDepartmentWiseAssignments(vesselId: string) {
			return dispatch(vdmDashboardActions.commands.vdmPICLoad(vesselId));
		},
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(PICDashboard);
